import type { TranslationKey } from './fr';
import { fleetEn } from '../../features/fleet/locales';
import { peopleEn } from '../../features/people/locales';
import { deploymentEn } from '../../features/deployment/i18n';
import { billingEn } from '../../features/billing/i18n';
import { settingsEn } from '../../features/settings/i18n';
import { satisfactionEn } from '../../features/satisfaction/locales';

export const en: Record<TranslationKey, string> = {
  'common.error': 'Something went wrong',
  'common.errorDescription': 'Unable to load data. Check your connection and try again.',
  'common.retry': 'Retry',
  'common.date': 'Date',

  'ui.close': 'Close',
  'ui.clear': 'Clear',
  'ui.notifications': 'Notifications',
  'ui.table.selectAll': 'Select all',
  'ui.table.selectRow': 'Select row',
  'ui.table.empty': 'No results',
  'ui.table.selected': '{count} selected',
  'ui.table.total': '{count} item(s)',
  'ui.table.pagination': 'Pagination',
  'ui.table.prev': 'Previous page',
  'ui.table.next': 'Next page',
  'ui.table.page': 'Page {page} of {count}',

  'status.online': 'Online',
  'status.offline': 'Offline',

  'nav.dashboard': 'Dashboard',
  'nav.fleet': 'Fleet',
  'nav.people': 'People',
  'nav.policies': 'Policies',
  'nav.audit': 'Audit & sessions',
  'nav.satisfaction': 'Satisfaction',
  'nav.deployment': 'Deployment',
  'nav.billing': 'Billing',
  'nav.settings': 'Settings',

  'shell.skipToContent': 'Skip to content',
  'shell.navigation': 'Main navigation',
  'shell.collapse': 'Collapse menu',
  'shell.expand': 'Expand menu',
  'shell.openMenu': 'Open menu',
  'shell.language': 'Language',
  'shell.themeDark': 'Switch to dark theme',
  'shell.themeLight': 'Switch to light theme',
  'shell.userMenu': 'User menu',
  'shell.profile': 'My profile',
  'shell.logout': 'Sign out',

  'org.current': 'Active organization',
  'org.switch': 'Switch organization',

  'search.placeholder': 'Search a device, a page…',
  'search.results': 'Search results',
  'search.none': 'No results',
  'search.page': 'Page',

  'forbidden.title': 'Access denied',
  'forbidden.description': 'Your role does not allow access to this area. Contact an organization administrator.',

  'placeholder.eyebrow': 'Coming soon',
  'placeholder.title': 'This screen is under development',
  'placeholder.description': 'Structure, navigation and permissions are in place. Planned screens for this area:',
  'placeholder.planned': 'Planned screens',

  'zone.fleet.title': 'Fleet',
  'zone.fleet.description': 'Organization devices, online status, groups and inventory.',
  'zone.fleet.screens': 'Device list and tree|Device details|Groups and tags|Inventory|Alerts|Actions (deploy, update, revoke)',
  'zone.people.title': 'People',
  'zone.people.description': 'Technicians, roles, invitations and access security.',
  'zone.people.screens': 'Users|Roles and permissions|Invitations|SSO|Trusted devices|IP / country blocking',
  'zone.policies.title': 'Policies',
  'zone.policies.description': 'Administration rules enforced on agents.',
  'zone.policies.screens': 'Policy templates|Assignment by group / role|Effect preview|Test mode',
  'zone.audit.title': 'Audit & sessions',
  'zone.audit.description': 'Audit log, session history and recordings.',
  'zone.audit.screens': 'Filterable log|Session details|Recordings|CSV / JSON export',
  'zone.deployment.title': 'Deployment',
  'zone.deployment.description': 'Mass enrollment and agent updates.',
  'zone.deployment.screens': 'MSI / PKG packages|Enrollment tokens|Stable / beta channels|Versions and progress',
  'zone.billing.title': 'Billing',
  'zone.billing.description': 'Subscription, usage and invoices.',
  'zone.billing.screens': 'Plan|Usage|Invoices|Payment method|Free trial',
  'zone.settings.title': 'Settings',
  'zone.settings.description': 'Organization, white label, integrations and API keys.',
  'zone.settings.screens': 'Organization and domain|White label|Integrations|API keys',

  'login.title': 'Sign in',
  'login.subtitle': 'Access your organization\'s administration console.',
  'login.email': 'Email address',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.error.credentials': 'Incorrect email or password.',
  'login.error.totp': 'Invalid verification code.',
  'login.error.expired': 'Verification expired. Please sign in again.',
  'login.error.noOrg': 'This account is not linked to any organization.',
  'login.error.generic': 'Unable to sign in right now. Please try again.',
  'login.hero.title': 'Run your fleet.',
  'login.hero.accent': 'In full control.',
  'login.hero.text': 'Who can connect to what, from where, with which limits, and at what cost: it is all managed here.',
  'login.hero.p1': 'Devices, groups and real-time status',
  'login.hero.p2': 'Roles, policies and audit log',
  'login.hero.p3': 'Mass deployment and billing',
  'login.totp.title': 'Two-step verification',
  'login.totp.subtitle': 'Enter the 6-digit code from your authenticator app for {email}.',
  'login.totp.code': 'Verification code',
  'login.totp.submit': 'Verify',
  'login.totp.back': 'Back',
  'login.totp.demoHint': 'Demo: the code is 123456.',
  'login.demo.title': 'Demo mode',
  'login.demo.hint': 'Simulated data. Password: siraj-demo. Click to fill in.',
  'login.demo.owner': 'Owner',
  'login.demo.supervisor': 'Supervisor',
  'login.demo.technician': 'Technician',
  'login.demo.billing': 'Billing',
  'login.noAccount': 'No account yet?',
  'login.createAccount': 'Create an account',

  'register.title': 'Create your console',
  'register.subtitle': 'Register your organization and become its owner.',
  'register.name': 'Your name',
  'register.email': 'Email address',
  'register.password': 'Password',
  'register.passwordHint': 'At least 8 characters.',
  'register.orgName': 'Organization name',
  'register.submit': 'Create account',
  'register.hasAccount': 'Already have an account?',
  'register.signIn': 'Sign in',
  'register.error.emailTaken': 'This email address is already in use.',
  'register.error.generic': 'Unable to register right now. Please try again.',

  'choosePlan.title': 'Choose a plan',
  'choosePlan.subtitle': 'To use the Console of {org}, start the PRO trial or subscribe to a paid plan.',
  'choosePlan.trial.title': '15-day PRO trial',
  'choosePlan.trial.desc': 'Every Pro plan feature for 15 days, no commitment. The trial is available once per organization.',
  'choosePlan.trial.used': 'This organization\'s trial has ended. Choose a paid plan to continue.',
  'choosePlan.trial.cta': 'Start the trial',
  'choosePlan.trial.done': 'PRO trial started for 15 days',
  'choosePlan.plan.pro': 'For small support teams.',
  'choosePlan.plan.team': 'For teams managing a large fleet.',
  'choosePlan.plan.enterprise': 'For large organizations, on quote.',
  'choosePlan.paid.cta': 'Subscribe',
  'choosePlan.paid.choose': 'Subscribe to the {plan} plan',
  'choosePlan.paid.done': '{plan} plan activated',
  'choosePlan.notAllowed': 'Only the owner or the billing manager can choose a plan. Contact them to open the Console.',
  'choosePlan.freeNote': 'Without a plan, the Console stays closed. Remote remains available with the free plan limits.',
  'choosePlan.switchOrg': 'Switch organization',
  'choosePlan.error': 'Action failed',
  'choosePlan.error.conflict': 'Action failed: the trial was already used or a plan is already subscribed',
  'invitation.accept.pending': 'Accepting the invitation…',
  'invitation.accept.done': 'Invitation accepted. Redirecting…',
  'invitation.accept.error': 'This invitation is invalid, expired or addressed to another account. Sign in with the invited email address.',
  'invitation.accept.retry': 'Retry',
  'selectOrg.title': 'Choose an organization',
  'selectOrg.subtitle': 'Hello {name}, which organization do you want to administer?',

  'dashboard.greeting': 'Hello {name}',
  'dashboard.subtitle': 'Here is the state of your fleet and service today.',
  'dashboard.kpis': 'Key indicators',
  'dashboard.devicesOnline': 'Devices online',
  'dashboard.devicesOnlineHint': '{pct}% of the fleet',
  'dashboard.sessionsToday': 'Sessions today',
  'dashboard.vsYesterday': 'vs yesterday',
  'dashboard.activeSessions': 'Ongoing sessions',
  'dashboard.activeSessionsHint': 'right now',
  'dashboard.alerts': 'Open alerts',
  'dashboard.alertsHint': 'including {count} critical',
  'dashboard.activity': '7-day activity',
  'dashboard.activityDesc': 'Remote sessions per day',
  'dashboard.activityAria': 'Bar chart: {total} sessions over the last 7 days',
  'dashboard.sessions': 'Sessions',
  'dashboard.devicesTouched': 'Distinct devices',
  'dashboard.usage': 'Usage',
  'dashboard.usageDesc': 'Licenses and AI credits this period',
  'dashboard.technicians': 'Technicians',
  'dashboard.aiCredits': 'AI credits',
  'dashboard.devices': 'Devices',
  'dashboard.unlimited': 'unlimited',
  'dashboard.alertsTitle': 'Alerts to handle',
  'dashboard.alertsDesc': 'Disk, memory, offline devices, outdated agents',
  'dashboard.noAlerts': 'No alerts: everything is in order',
  'dashboard.service': 'Service status',
  'dashboard.serviceDesc': 'Siraj components',
  'dashboard.updatedAt': 'Updated at {time}',

  'severity.critical': 'Critical',
  'severity.warning': 'Warning',
  'severity.info': 'Info',
  'alert.offline': 'Offline for {value} day(s)',
  'alert.disk': 'Disk {value}% full',
  'alert.ram': 'Memory {value}% used',
  'alert.agent_outdated': 'Outdated Siraj agent',
  'alert.policy_denied': 'Access denied by a policy',

  'service.relay': 'Relay',
  'service.rendezvous': 'Rendezvous',
  'service.api': 'API',
  'service.ai': 'AI assistant',
  'service.status.operational': 'Operational',
  'service.status.degraded': 'Degraded',
  'service.status.outage': 'Outage',
  // >>> zone policies
  'policies.title': 'Policy templates',
  'policies.subtitle': 'Administration rules enforced on agents: quality caps, transfers, privacy, schedules.',
  'policies.new': 'New policy',
  'policies.newDesc': 'Created as a draft in test mode: nothing is enforced until you confirm.',
  'policies.name': 'Name',
  'policies.description': 'Description',
  'policies.status': 'Status',
  'policies.status.active': 'Active',
  'policies.status.draft': 'Draft',
  'policies.version': 'Version',
  'policies.targets': 'Targets',
  'policies.targetsAll': 'Whole fleet',
  'policies.targetsCount': '{count} target(s)',
  'policies.updated': 'Updated',
  'policies.actions': 'Actions',
  'policies.default': 'Default',
  'policies.dryRun': 'Test mode',
  'policies.empty': 'No policies yet.',
  'policies.cancel': 'Cancel',
  'policies.noDefault': 'No default policy',
  'policies.noDefaultDesc': 'A policy without targets applies to the whole fleet. Create a permissive default policy (no restriction) as a safe baseline.',
  'policies.createDefault': 'Create the permissive default policy',
  'policies.defaultName': 'Default policy',
  'policies.defaultDesc': 'Permissive: applies to all devices without a specific policy.',
  'policies.defaultCreated': 'Default policy created',
  'policies.delete': 'Delete {name}',
  'policies.deleteTitle': 'Delete "{name}"?',
  'policies.deleteDesc': 'Affected agents fall back to the default policy. Version history stays in the audit log.',
  'policies.apply.needName': "A policy name is required to apply it.",
  'policies.apply.noChanges': "No change to apply: edit the policy first.",
  'policies.deleteDescDefault': "This default policy applies to all agents: deleting it returns them to system settings.",
  'policies.deleteConfirm': 'Delete',
  'policies.deleted': 'Policy deleted',
  'policies.deleteError': 'Could not delete the policy',
  'policies.notFound': 'Policy not found',
  'policies.backToList': 'All policies',
  'policies.editorSubtitle': 'Version {version} · every change creates a new version',
  'policies.general': 'General',
  'policies.dryRunLabel': 'Test mode (dry run)',
  'policies.dryRunDesc': 'Agents log what would be blocked without enforcing anything. Recommended before any restriction.',
  'policies.tabs': 'Policy sections',
  'policies.tab.rules': 'Rules',
  'policies.tab.assign': 'Assignment and preview',
  'policies.tab.history': 'History',
  'policies.tab.delivery': 'Delivery',
  'policies.saveDraft': 'Save draft',
  'policies.apply': 'Apply…',
  'policies.applyChanges': 'Apply changes…',
  'policies.backToDraft': 'Revert to draft',
  'policies.resetPermissive': 'Reset to permissive rules',
  'policies.saved': 'Draft saved',
  'policies.applied': 'Policy applied: rollout in progress',
  'policies.reverted': 'Policy reverted to draft',
  'policies.saveError': 'Could not save. Please try again.',
  'error.featureUnavailable': 'This feature is not included in your current plan. Upgrade your plan to use it.',
  'error.quotaExceeded': 'You have reached your plan limit. Upgrade your plan to raise it.',
  'error.upgradeLink': 'See plans',
  'error.invalidResponse': 'Unexpected response from the server. Please try again shortly.',
  'policies.forbidden': 'You do not have permission to change policies.',
  'policies.rules.emptyUnlimited': 'Empty = unlimited',
  'policies.rules.transfers': 'Transfers and clipboard',
  'policies.rules.transfersDesc': 'Blocked file types, maximum size and copy-paste direction.',
  'policies.rules.privacy': 'Privacy and control',
  'policies.rules.privacyDesc': 'Privacy screen, recording and manager approval.',
  'policies.rules.windows': 'Access time windows',
  'policies.rules.windowsDesc': 'Apply to the policy targets (technicians, roles, groups). Outside a window, access is denied.',
  'policies.rule.blockedFileTypes': 'Blocked file types',
  'policies.rule.blockedFileTypesHint': 'Comma-separated extensions',
  'policies.rule.maxTransferMb': 'Maximum size per transfer (MB)',
  'policies.rule.clipboard': 'Clipboard',
  'policies.rule.privacyScreen': 'Privacy screen',
  'policies.rule.privacyScreenDesc': 'Blanks the device screen during the session.',
  'policies.rule.recordingRequired': 'Recording required',
  'policies.rule.recordingRequiredDesc': 'Every session is recorded (with retention and encryption).',
  'policies.rule.requireApproval': 'Manager approval',
  'policies.rule.requireApprovalDesc': 'A sensitive connection waits for a manager\'s approval.',
  'policies.rule.approverRole': 'Approver role',
  'policies.rule.approverNone': 'Choose a role',
  'policies.rule.approverRoleId': 'Approver role',
  'policies.rule.accessWindows': 'Time windows',
  'policies.rule.targets': 'Assignment',
  'policies.rule.enabled': 'Status',
  'policies.rule.dryRun': 'Test mode',
  'policies.clipboard.both': 'Both directions',
  'policies.clipboard.to_host': 'To the device only',
  'policies.clipboard.from_host': 'From the device only',
  'policies.clipboard.none': 'Blocked',
  'policies.window.add': 'Add a window',
  'policies.window.none': 'No window: access allowed at any time.',
  'policies.window.days': 'Days',
  'policies.window.start': 'Start',
  'policies.window.end': 'End',
  'policies.window.remove': 'Remove window',
  'policies.day.0': 'Sun',
  'policies.day.1': 'Mon',
  'policies.day.2': 'Tue',
  'policies.day.3': 'Wed',
  'policies.day.4': 'Thu',
  'policies.day.5': 'Fri',
  'policies.day.6': 'Sat',
  'policies.assign.title': 'Assignment',
  'policies.assign.desc': 'By group, role, technician or device. With no target, the policy applies to the whole fleet.',
  'policies.assign.groups': 'Device groups',
  'policies.assign.noGroups': 'No groups',
  'policies.assign.roles': 'Roles',
  'policies.assign.members': 'Technicians',
  'policies.assign.devices': 'Devices',
  'policies.assign.searchMembers': 'Search technicians',
  'policies.assign.searchDevices': 'Search devices',
  'policies.assign.selectedDevices': 'Selected devices',
  'policies.preview.title': 'Effect preview',
  'policies.preview.desc': 'Estimates affected devices and users and detects conflicts before anything is applied.',
  'policies.preview.run': 'Compute preview',
  'policies.preview.devices': 'Devices affected',
  'policies.preview.members': 'Users affected',
  'policies.preview.conflicts': 'Conflicts',
  'policies.preview.conflict': 'Overlaps "{name}" on {count} device(s): the most restrictive rule wins.',
  'policies.preview.dryRunNote': 'Test mode: nothing is actually blocked, agents only log.',
  'policies.preview.error': 'Preview unavailable. You can try again.',
  'policies.warn.access_windows': 'Time windows will deny access outside the defined ranges.',
  'policies.warn.no_approver': 'No member holds the approver role: sensitive connections would stay blocked.',
  'policies.warn.clipboard_none': 'The clipboard is blocked in both directions.',
  'policies.warn.no_target': 'No device is targeted by this assignment.',
  'policies.risk.title': 'Lockout risks',
  'policies.risk.windows_invalid': 'A time window ends before or at its start.',
  'policies.risk.windows_no_days': 'A time window has no day: it never allows access.',
  'policies.risk.windows_narrow': 'Windows cover less than 20 h per week: access will be very limited.',
  'policies.risk.approval_no_approver': 'Approval is required without an approver role: nobody could approve.',
  'policies.risk.global_restrictive': 'This restrictive policy targets the whole fleet, administrators included.',
  'policies.risk.ack': 'I understand the lockout risk and want to apply anyway.',
  'policies.apply.title': 'Apply the policy?',
  'policies.apply.titleDryRun': 'Activate in test mode?',
  'policies.apply.desc': 'Check the expected effect. The new version will be pushed to online agents.',
  'policies.apply.confirm': 'Apply now',
  'policies.apply.confirmDryRun': 'Activate in test mode',
  'policies.history.title': 'Version history',
  'policies.history.desc': 'Every change is kept. A rollback creates a new version.',
  'policies.history.empty': 'No versions',
  'policies.history.current': 'Current version',
  'policies.history.changed': 'Changed:',
  'policies.history.rollbackOf': 'Rollback to v{version}',
  'policies.history.rollback': 'Roll back to v{version}',
  'policies.history.rollbackTitle': 'Roll back to version {version}?',
  'policies.history.rollbackDesc': 'Rules and assignment from v{version} will be restored as a new version v{next} and pushed to agents.',
  'policies.history.rollbackConfirm': 'Roll back',
  'policies.history.rolledBack': 'Version {version} restored',
  'policies.history.rollbackError': 'Rollback failed',
  'policies.delivery.title': 'Rollout to agents',
  'policies.delivery.desc': 'Each agent\'s acknowledgement of the current version.',
  'policies.delivery.dryRunDesc': 'Test mode: agents acknowledge but block nothing.',
  'policies.delivery.none': 'No rollout',
  'policies.delivery.noneDesc': 'A draft is not pushed to any agent. Apply the policy to roll it out.',
  'policies.delivery.progress': '{done} / {total} agents acknowledged ({pct}%)',
  'policies.delivery.device': 'Device',
  'policies.delivery.status': 'State',
  'policies.delivery.ackedAt': 'Acknowledged at',
  'policies.delivery.acknowledged': 'Received',
  'policies.delivery.pending': 'Pending',
  'policies.delivery.offline': 'Offline',
  'policies.delivery.failed': 'Failed',
  // <<< zone policies
  // >>> zone fleet
  ...fleetEn,
  // <<< zone fleet
  // >>> zone people
  ...peopleEn,
  // <<< zone people
  // >>> zone deployment
  ...deploymentEn,
  // <<< zone deployment
  // >>> zone billing
  ...billingEn,
  // <<< zone billing
  // >>> zone audit
  'audit.title': 'Audit & sessions',
  'audit.subtitle': 'Signed audit log, session history, recordings and compliance reports.',
  'audit.tabs': 'Audit and sessions sections',
  'audit.tab.journal': 'Audit log',
  'audit.tab.sessions': 'Sessions',
  'audit.tab.compliance': 'Compliance',
  'audit.cancel': 'Cancel',
  'audit.journal': 'Audit log',
  'audit.search': 'Search (actor, action, target, IP)',
  'audit.count': '{count} event(s)',
  'audit.loading': 'Loading…',
  'audit.empty': 'No event matches the filters.',
  'audit.filter.action': 'Action',
  'audit.filter.actor': 'Actor',
  'audit.filter.outcome': 'Outcome',
  'audit.filter.device': 'Device',
  'audit.filter.devicePlaceholder': 'Device name',
  'audit.filter.signature': 'Signature',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.filter.all': 'All',
  'audit.filter.reset': 'Reset filters',
  'audit.col.time': 'Time',
  'audit.col.actor': 'Actor',
  'audit.col.action': 'Action',
  'audit.col.target': 'Target',
  'audit.col.outcome': 'Outcome',
  'audit.col.signature': 'Signature',
  'audit.outcome.success': 'Success',
  'audit.outcome.failure': 'Failure',
  'audit.outcome.denied': 'Denied',
  'audit.actorType.member': 'member',
  'audit.actorType.device': 'device',
  'audit.actorType.system': 'system',
  'audit.actorType.api': 'API',
  'audit.signature.valid': 'Signature verified',
  'audit.signature.invalid': 'Invalid signature',
  'audit.signature.validDesc': 'The hash chain is intact: the event has not been altered since it was recorded.',
  'audit.signature.invalidDesc': 'The hash chain does not match: the event may have been tampered with or lost. Contact Siraj support and keep a copy of the JSON.',
  'audit.event.title': 'Event details',
  'audit.event.open': 'Open event {action}',
  'audit.event.origin': 'Origin',
  'audit.event.json': 'Raw event (JSON)',
  'audit.event.copy': 'Copy JSON',
  'audit.event.copied': 'JSON copied',
  'audit.event.copyError': 'Could not copy',
  'audit.export.button': 'Export',
  'audit.export.csv': 'Export as CSV',
  'audit.export.json': 'Export as JSON',
  'audit.export.done': 'Export downloaded: {filename}',
  'audit.export.error': 'Export failed',
  'audit.session.title': 'Session details',
  'audit.session.search': 'Search (device, technician, ticket)',
  'audit.session.count': '{count} session(s)',
  'audit.session.empty': 'No session matches the filters.',
  'audit.session.open': 'Open session on {device}',
  'audit.session.start': 'Start',
  'audit.session.end': 'End',
  'audit.session.ongoing': 'ongoing',
  'audit.session.duration': 'Duration',
  'audit.session.technician': 'Technician',
  'audit.session.device': 'Device',
  'audit.session.deletedDevice': 'Deleted device',
  'audit.session.modeCol': 'Mode',
  'audit.session.statusCol': 'Status',
  'audit.session.recording': 'Recording',
  'audit.session.recorded': 'Recorded',
  'audit.session.ticketCol': 'Ticket',
  'audit.session.actions': 'Actions performed',
  'audit.session.transferred': 'Data transferred',
  'audit.session.mode.attended': 'Attended',
  'audit.session.mode.unattended': 'Unattended',
  'audit.session.status.active': 'Active',
  'audit.session.status.ended': 'Ended',
  'audit.session.status.failed': 'Failed',
  'audit.session.participants': 'Participants',
  'audit.session.role.technician': 'technician',
  'audit.session.role.client': 'client',
  'audit.session.role.observer': 'observer',
  'audit.session.summary': 'AI summary',
  'audit.session.noSummary': 'No summary available.',
  'audit.session.rating': 'Client rating',
  'audit.session.ratingValue': '{value} out of 5',
  'audit.session.noRating': 'No client rating.',
  'audit.session.clientComment': 'Customer comment',
  'audit.session.ticket': 'Ticket report',
  'audit.session.ticketDownload': 'Download report (JSON)',
  'audit.session.noTicket': 'No ticket linked to this session.',
  'audit.rec.title': 'Recording',
  'audit.rec.none': 'This session was not recorded.',
  'audit.rec.consent': 'Client consent',
  'audit.rec.consent.granted': 'Granted',
  'audit.rec.consent.pending': 'Pending',
  'audit.rec.consent.denied': 'Denied',
  'audit.rec.size': 'Size',
  'audit.rec.sizeValue': '{size} MB · {duration}',
  'audit.rec.encryption': 'Encryption',
  'audit.rec.encrypted': 'Encrypted at rest',
  'audit.rec.notEncrypted': 'Not encrypted',
  'audit.rec.retention': 'Retention',
  'audit.rec.retentionUntil': 'Until {date} ({days} day(s) left)',
  'audit.rec.purged': 'Purged on {date} (retention ended)',
  'audit.rec.purgedNote': 'The recording was deleted when retention ended.',
  'audit.rec.pendingNote': 'Playback locked: the client has not yet consented to review.',
  'audit.rec.deniedNote': 'Playback unavailable: the client refused review.',
  'audit.rec.play': 'Play recording',
  'audit.rec.noPermission': '"Recordings" permission required.',
  'audit.rec.playTitle': 'Play the recording',
  'audit.rec.playDesc': 'Playback is logged in the audit log with your reason.',
  'audit.rec.reason': 'Reason for review',
  'audit.rec.reasonHint': 'At least 5 characters (e.g. dispute, ticket, training).',
  'audit.rec.playConfirm': 'Play',
  'audit.rec.consentRequired': 'The client has not consented to review.',
  'audit.rec.accessError': 'Could not access the recording.',
  'audit.rec.player': 'Recording player',
  'audit.rec.pause': 'Pause',
  'audit.rec.resume': 'Play',
  'audit.rec.seek': 'Playback position',
  'audit.rec.expires': 'Temporary access valid until {time}',
  'audit.compliance.period': 'Period',
  'audit.compliance.lastDays': 'Last {days} days',
  'audit.compliance.download': 'Download report',
  'audit.compliance.exportEvents': 'Export events',
  'audit.compliance.access': 'Access indicators',
  'audit.compliance.events': 'Audited events',
  'audit.compliance.sessionsHint': '{count} session(s) in the period',
  'audit.compliance.denied': 'Access denied',
  'audit.compliance.deniedHint': 'Blocked by a policy or role',
  'audit.compliance.failures': 'Failures',
  'audit.compliance.failedLogins': 'including {count} failed sign-in(s)',
  'audit.compliance.invalid': 'Invalid signatures',
  'audit.compliance.invalidHint': 'Hash-chain integrity',
  'audit.compliance.invalidAlert': '{count} event(s) with an invalid signature in the period: review required.',
  'audit.compliance.review': 'Review events',
  'audit.compliance.retention': 'Data retention',
  'audit.compliance.retentionDesc': 'Retention periods applied to your organization (adjustable in settings).',
  'audit.compliance.auditRetention': 'Audit log',
  'audit.compliance.recRetention': 'Session recordings',
  'audit.compliance.days': '{days} days',
  'audit.compliance.integrity': 'Integrity',
  'audit.compliance.integrityDesc': 'Every event is signed and chained; any tampering is detected.',
  // <<< zone audit

  // Zone Paramètres
  ...settingsEn,
  ...satisfactionEn,
};
